import {
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContainerPainel from 'components/container_painel';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IQuestoesAmbientais, useQuestoesAmbientaisStore } from 'stores/forms/questoes_ambientais.store';

interface ProblemaAmbiental {
  key: keyof IQuestoesAmbientais;
  label: string;
  prioridade: number | null;
}

const FormQuestoesAmbientaisVisualizar: React.FC = () => {
  const { uuid } = useParams();
  const { questoes, getQuestoes } = useQuestoesAmbientaisStore();

  const problemasAmbientais: {
    key: keyof IQuestoesAmbientais;
    label: string;
  }[] = [
      {
        key: 'prioridadePoucosImoveisRegularizados',
        label: 'Poucos imóveis rurais regularizados ambientalmente',
      },
      {
        key: 'prioridadeSemTratamentoResiduos',
        label: 'Ausência de tratamento de resíduos sólidos (lixo)',
      },
      {
        key: 'prioridadeDegradacaoApp',
        label: 'Degradação das Áreas de Preservação Permanente (APP)',
      },
      {
        key: 'prioridadeDegradacaoSolo',
        label: 'Degradação do solo (processos erosivos, compactação, aventização, perda de fertilidade etc.)',
      },
      {
        key: 'prioridadeDesmatamentoIlegal',
        label: 'Desmatamento ilegal e degradação florestal',
      },
      {
        key: 'prioridadePerdaBiodiversidade',
        label: 'Perda da biodiversidade',
      },
      {
        key: 'prioridadeCriseHidrica',
        label: 'Crise Hídrica e perca de água',
      },
      {
        key: 'prioridadePoluicaoAgua',
        label: 'Poluição e contaminação da água',
      },
      {
        key: 'prioridadePoluicaoSolo',
        label: 'Poluição e contaminação do solo',
      },
      {
        key: 'prioridadeQueimadas',
        label: 'Queimadas e incêndios florestais',
      },
      {
        key: 'prioridadeUsoAgrotoxicos',
        label: 'Uso indiscriminado de agrotóxicos',
      },
      {
        key: 'prioridadeMudancasClimaticas',
        label: 'Mudanças climáticas / Eventos extremos de seca e cheias',
      },
    ];

  // Organizar problemas por prioridade (1-12)
  const problemasPriorizados: ProblemaAmbiental[] = problemasAmbientais
    .map(problema => ({
      ...problema,
      prioridade: questoes[problema.key] || null,
    }))
    .filter(problema => problema.prioridade !== null && problema.prioridade !== '')
    .sort((a, b) => (a.prioridade || 0) - (b.prioridade || 0));

  // Verificar se tem outro problema (prioridade 13 = Sim)
  const temOutroProblema = questoes.prioridadeOutroProblema === 13;
  const descricaoOutroProblema = questoes.descricaoOutroProblema || '';

  useEffect(() => {
    if (uuid) {
      getQuestoes(uuid);
    }
  }, [uuid, getQuestoes]);

  return (
    <ContainerPainel
      pagina='Visualização - Formulário de Questões Ambientais'
      showBackButton={true}
    >
      <Box sx={{ padding: 3 }}>
        {/* Header */}
        <Box sx={{ backgroundColor: '#0048b6', p: 2, borderRadius: 1, mb: 3 }}>
          <Typography
            variant='h4'
            component='h1'
            align='center'
            sx={{ color: 'white' }}
          >
            QUESTÕES AMBIENTAIS
          </Typography>
          <Typography
            variant='subtitle1'
            align='center'
            sx={{ color: 'white' }}
          >
            Visualização das Prioridades Ambientais do Município
          </Typography>
        </Box>

        {/* Informações do formulário */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            ml: 7,
          }}
        >
          <Typography
            variant='h5'
            gutterBottom
            sx={{ color: '#1976d2', fontSize: '0.9rem' }}
          >
            Problemas ambientais mais significativos no meio rural do município
            (1 para o mais significativo, 2 para o segundo mais significativo e assim por diante)
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            ml: 7,
          }}
        >
          <Typography sx={{ mr: 2, fontWeight: 'bold' }}>
            Ano de referência:
          </Typography>
          <Typography sx={{ mr: 2 }}>
            {questoes.anoReferencia || questoes.ano_referencia_do_questionario || '2025'}
          </Typography>
        </Box>

        {/* Legenda */}
        <Box
          sx={{
            backgroundColor: '#f5f5f5',
            p: 2,
            mb: 3,
            borderRadius: 1,
            ml: 6,
          }}
        >
          <Typography variant='body2'>
            - Problemas ambientais ordenados por prioridade (1 = mais significativo)
            <br />
            - Modo somente leitura - dados não podem ser editados
          </Typography>
        </Box>

        {/* Lista de problemas priorizados */}
        <Stack spacing={2} sx={{ ml: 6, mr: 6 }}>
          {problemasPriorizados.length > 0 ? (
            <>
              <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
                Problemas Ambientais Priorizados:
              </Typography>

              {problemasPriorizados.map((problema, index) => (
                <Box
                  key={problema.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 1,
                    boxShadow: 1,
                    gap: 2,
                  }}
                >
                  <Chip
                    label={`${problema.prioridade}º`}
                    color="primary"
                    sx={{
                      minWidth: '50px',
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}
                  />
                  <Typography sx={{ flex: 1, fontWeight: 'bold' }}>
                    {problema.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: '#4caf50',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}
                  >
                    Prioridade {problema.prioridade}
                  </Typography>
                </Box>
              ))}
            </>
          ) : (
            <Box
              sx={{
                p: 3,
                backgroundColor: '#fff3e0',
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant='h6' color='text.secondary'>
                Nenhum problema ambiental foi priorizado neste formulário.
              </Typography>
            </Box>
          )}

          {/* Outro problema ambiental */}
          {temOutroProblema && (
            <Box sx={{ mt: 3 }}>
              <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 2 }}>
                Outro Problema Ambiental:
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  backgroundColor: 'white',
                  borderRadius: 1,
                  boxShadow: 1,
                  gap: 2,
                }}
              >
                <Chip
                  label={questoes.prioridadeOutroProblema === 13 ? "Sim" : "Não"}
                  color={questoes.prioridadeOutroProblema === 13 ? "success" : "default"}
                  sx={{
                    minWidth: '50px',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                    Outro problema ambiental especificado:
                  </Typography>
                  <TextField
                    fullWidth
                    value={descricaoOutroProblema}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: '#9c27b0',
                    fontSize: '0.9rem',
                    fontStyle: 'italic'
                  }}
                >
                  Outro problema
                </Typography>
              </Box>
            </Box>
          )}
        </Stack>


      </Box>
    </ContainerPainel>
  );
};

export default FormQuestoesAmbientaisVisualizar;
